<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import type { RouteRecordNormalized } from 'vue-router'
import { useRouter } from 'vue-router'
import { useUIStore } from '@/stores/ui'
import { useTabStore } from '@/stores/tab'

type MenuItem = Pick<RouteRecordNormalized, 'name' | 'meta' | 'path'> & { key: string, children: MenuItem[] }

const { isCollapsed } = storeToRefs(useUIStore())

const userStore = useUserStore()

const { menus } = storeToRefs(userStore)

const tabStore = useTabStore()

const router = useRouter()

const menuTree = computed(() => {
  const temp: MenuItem[] = []
  const rawRoutes = router.getRoutes()
  const items = rawRoutes.map(r => ({ ...r, key: r.path, children: [] as MenuItem[] }))
  items.forEach((r) => {
    if (r.meta?.isLayout
      || r.meta?.hideInMenu
      || !menus.value.includes(r.path)
    ) return

    const path = r.path
    const level = path.split('/').length - 2
    if (level === 0) {
      temp.push({ ...r })
    }
    else {
      const parentPath = path.slice(0, path.lastIndexOf('/'))
      const parent = items.find(item => item.path === parentPath)!
      parent.children.push(r)
    }
  })
  temp.sort((a, b) => (b.meta.order || 0) - (a.meta.order || 0))
  // console.log('temp', temp)
  return temp
})

// console.log({ routes: routes.value })

const defaultActive = computed(() => {
  return tabStore.tabs[tabStore.currentTabIndex]?.path
})

function onOpen() { }

function onClose() { }

function onSelect(key: string, keyPath: string[]) {
  // console.log('onSelect', { key, keyPath })

  const item = router.getRoutes().find(item => item.path === key)!

  if (!item)
    return

  const { children, ...data } = item

  if (!data)
    return

  const tab = { ...data, fullPath: data.path }

  router.push(tab.fullPath)
}
</script>

<template>
  <el-menu
    class="el-menu-vertical-demo" :default-active="defaultActive" :collapse="isCollapsed" @select="onSelect"
    @open="onOpen" @close="onClose"
  >
    <template v-for="(item) in menuTree" :key="item.path">
      <el-sub-menu v-if="item.children?.length" :index="item.path">
        <template #title>
          <el-icon>
            <div icon-base :class="item.meta.icon" />
          </el-icon>
          <span>{{ item.meta.title }}</span>
        </template>
        <div v-for="subItem in item.children" :key="subItem.path">
          <el-menu-item :index="subItem.path">
            <el-icon v-if="subItem.meta?.icon">
              <div icon-base :class="subItem.meta.icon" />
            </el-icon>
            <template #title>
              <span>{{ subItem.meta?.title }}</span>
            </template>
          </el-menu-item>
        </div>
      </el-sub-menu>
      <el-menu-item v-else :index="item.path">
        <el-icon>
          <div icon-base :class="item.meta.icon" />
        </el-icon>
        <template #title>
          <span>{{ item.meta.title }}</span>
        </template>
      </el-menu-item>
    </template>
  </el-menu>
</template>

<style scoped>
.el-menu {
  border-right: 0;
}
</style>
